import { KeyValue } from "@almamedia/amr-adtech-stack/lib/services/ads";
import { GetKvs } from "@almamedia/amr-adtech-stack/lib/services/ads/adsservice";
import { IAdtechStack } from "@almamedia/amr-adtech-stack/lib/services/adtechstack";
import { StackContext } from "../context";
import { ICustomVariables } from "../data-layer/data-layer";
import { SiteNativeTemplate } from "../native/native";
import {
  constructionYearGroups,
  convertLocationName,
  getRoomCount,
  getValueGroupRange,
  priceMap,
  roomCountConverter,
} from "./etuovi/helpers";

function synthesizeTargetingParam(
  key: string,
  getter: (
    context: IAdtechStack<ICustomVariables, SiteNativeTemplate>
  ) => unknown | undefined,
  context: StackContext,
  params?: KeyValue[]
): KeyValue[] {
  const newParam = {
    key,
    value: getter(context),
  };

  return [...(params ?? []), newParam] as KeyValue[];
}

const formEoAdTargetingParams = (c: StackContext): KeyValue[] => {
  const dl = c.services.dataLayerService?.dataLayer;
  let params: KeyValue[] = [];

  params = synthesizeTargetingParam(
    "eoPrimaryCategory",
    (context) => context.services.dataLayerService?.dataLayer.primaryCategory,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eosubCategory",
    (context) => context.services.dataLayerService?.dataLayer.subCategory,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoTags",
    (context) => context.services.dataLayerService?.dataLayer.pageTags,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "URL",
    (context) => context.services.dataLayerService?.dataLayer.locationHref,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoConditions",
    (context) => context.services.dataLayerService?.dataLayer.overallConditions,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "iabTier2",
    () => "Buying/Selling Homes",
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoShore",
    (context) => context.services.dataLayerService?.dataLayer.shore,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoCountries",
    (context) => {
      let locs: string[] = [];
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      if (eoLocation?.countryCode) {
        locs.push(convertLocationName(eoLocation.countryCode));
      }
      locs = [
        ...locs,
        ...(eoLocation?.countries
          .map(convertLocationName)
          .filter((country) => !locs.includes(country)) ?? []),
      ];

      return locs;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoIsAbroad",
    (context) => {
      const loc =
        context.services.dataLayerService?.dataLayer.locationTargeting;
      return loc?.countryCode !== "FI" ? "true" : "false";
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoRegions",
    (context) => {
      let locs: string[] = [];
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      if (eoLocation?.regionName) {
        locs.push(convertLocationName(eoLocation.regionName));
      }

      locs = [
        ...locs,
        ...(eoLocation?.regions
          .map(convertLocationName)
          .filter((r) => !locs.includes(r)) ?? []),
      ];

      return locs;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "province_eo",
    (context) => {
      let locs: string[] = [];
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      if (eoLocation?.regionName) {
        locs.push(convertLocationName(eoLocation.regionName));
      }

      locs = [
        ...locs,
        ...(eoLocation?.regions
          .map(convertLocationName)
          .filter((r) => !locs.includes(r)) ?? []),
      ];

      return locs;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoMunicipalities",
    (context) => {
      let locs: string[] = [];
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      if (eoLocation?.municipalityName) {
        locs.push(convertLocationName(eoLocation.municipalityName));
      }

      locs = [
        ...locs,
        ...(eoLocation?.municipalities
          .map(convertLocationName)
          .filter((m) => !locs.includes(m)) ?? []),
      ];

      return locs;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "municipality_eo",
    (context) => {
      let locs: string[] = [];
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      if (eoLocation?.municipalityName) {
        locs.push(convertLocationName(eoLocation.municipalityName));
      }

      locs = [
        ...locs,
        ...(eoLocation?.municipalities
          .map(convertLocationName)
          .filter((m) => !locs.includes(m)) ?? []),
      ];

      return locs;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoDistricts",
    (context) => {
      let locs: string[] = [];
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      if (eoLocation?.districtName) {
        locs.push(convertLocationName(eoLocation.districtName));
      }

      locs = [
        ...locs,
        ...(eoLocation?.districts
          .map(convertLocationName)
          .filter((d) => !locs.includes(d)) ?? []),
      ];

      return locs;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "district_eo",
    (context) => {
      let locs: string[] = [];
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      if (eoLocation?.districtName) {
        locs.push(convertLocationName(eoLocation.districtName));
      }

      locs = [
        ...locs,
        ...(eoLocation?.districts
          .map(convertLocationName)
          .filter((d) => !locs.includes(d)) ?? []),
      ];

      return locs;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoNicknames",
    (context) => {
      let locs: string[] = [];
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      locs = [
        ...locs,
        ...(eoLocation?.nicknames
          .map(convertLocationName)
          .filter((d) => !locs.includes(d)) ?? []),
      ];

      return locs;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoPostCode",
    (context) => {
      const eoLocation =
        context.services.dataLayerService?.dataLayer.locationTargeting;

      if (eoLocation?.postCodeAndOffice) {
        return eoLocation.postCodeAndOffice.split(" ")[0];
      }
      return undefined;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoNewProperty",
    (context) => context.services.dataLayerService?.dataLayer.newProperty,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoPropertyType",
    (context) => context.services.dataLayerService?.dataLayer.propertyType,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoPropertySubTypes",
    (context) => {
      const propertySubTypes =
        context.services.dataLayerService?.dataLayer.propertySubTypes;
      if (propertySubTypes) {
        return propertySubTypes;
      }
      return [];
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoPrice",
    () => {
      const priceItem = dl?.priceItem;
      if (priceItem) {
        return getValueGroupRange(priceItem, priceItem, priceMap);
      }
      const priceSearchMin = dl?.priceSearchMin;
      const priceSearchMax = dl?.priceSearchMax;
      if (priceSearchMin || priceSearchMax) {
        return getValueGroupRange(priceSearchMin, priceSearchMax, priceMap);
      }

      return [];
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoRoomCount",
    () => {
      return getRoomCount(roomCountConverter, dl?.roomCount);
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoArea",
    () => {
      const areaItem = dl?.areaItem;
      if (areaItem) {
        return getValueGroupRange(areaItem, areaItem, priceMap);
      }
      const areaSearchMin = dl?.areaSearchMin;
      const areaSearchMax = dl?.areaSearchMax;
      if (areaSearchMin || areaSearchMax) {
        return getValueGroupRange(areaSearchMin, areaSearchMax, priceMap);
      }

      return [];
    },
    c,
    params
  );
  // TODO: Check logic with EO
  params = synthesizeTargetingParam(
    "eoPrivateSeller",
    () => {
      if (dl?.officeId && dl.officeId !== "NOT_AVAILABLE") {
        return "false";
      }
      return "true";
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoOfficeId",
    () => {
      if (dl?.officeId && dl.officeId !== "NOT_AVAILABLE") {
        return dl?.officeId;
      }
      return undefined;
    },
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoCustomerGroupId",
    (context) => context.services.dataLayerService?.dataLayer.customerGroupId,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoOwnership",
    (context) => context.services.dataLayerService?.dataLayer.ownership,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoHeat",
    (context) => context.services.dataLayerService?.dataLayer.heatingSystems,
    c,
    params
  );
  params = synthesizeTargetingParam(
    "eoPublished",
    (context) => context.services.dataLayerService?.dataLayer.published,
    c,
    params
  );

  if (dl?.loanCalculatorBank) {
    params = synthesizeTargetingParam(
      "loanCalculatorBank",
      (context) =>
        context.services.dataLayerService?.dataLayer.loanCalculatorBank,
      c,
      params
    );
  }

  if (dl?.constructionYearSearchMin || dl?.constructionYearSearchMax) {
    const constructionYearMin = dl.constructionYearSearchMin || undefined;
    const constructionYearMax = dl.constructionYearSearchMax || undefined;
    params = synthesizeTargetingParam(
      "eoConstructionYear",
      () => {
        return getValueGroupRange(
          constructionYearMin,
          constructionYearMax,
          constructionYearGroups
        );
      },
      c,
      params
    );
  }
  if (dl?.constructionYearItem) {
    const constructionYearMinMax = dl.constructionYearItem;
    params = synthesizeTargetingParam(
      "eoConstructionYear",
      () => {
        return getValueGroupRange(
          constructionYearMinMax,
          constructionYearMinMax,
          constructionYearGroups
        );
      },
      c,
      params
    );
  }

  return params;
};

const syncKvs: GetKvs<ICustomVariables, SiteNativeTemplate> = (
  context: StackContext
): KeyValue[] => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dl = context.services.dataLayerService!.dataLayer;

  const kvs = [
    {
      key: "primaryCategory",
      value: [dl.primaryCategory],
    },
    {
      key: "subCategory",
      value: [dl.subCategory],
    },
    {
      key: "URL",
      value: [dl.locationHref],
    },
    {
      key: "pageId",
      value: [dl.pageId ?? ""],
    },
  ];

  return [
    ...kvs,
    ...formEoAdTargetingParams(context).map((kv) => ({
      key: `${kv.key}CT`,
      value: kv.value,
    })),
  ];
};

const asyncKvs = async (context: StackContext): Promise<KeyValue[]> => {
  const consent = await context.services.almaCmpService?.getPublisherConsents();
  if (consent?.standard[2] && consent.standard[3]) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const dl = context.services.dataLayerService!.dataLayer;

    const kvs = [
      {
        key: "primaryCategory",
        value: [dl.primaryCategory],
      },
      {
        key: "subCategory",
        value: [dl.subCategory],
      },
      {
        key: "URL",
        value: [dl.locationHref],
      },
      {
        key: "pageId",
        value: [dl.pageId ?? ""],
      },
    ];

    return [...kvs, ...formEoAdTargetingParams(context)];
  }
  return [];
};

const adTargeting = {
  syncKvs,
  asyncKvs,
};

export default adTargeting;
